import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Megaphone, Plus, Trash2, Clock, Users, Send,
    X, Loader2, CheckCircle, Search, User
} from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const AdminAnnouncements = () => {
    const { token } = useAuth();
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [sending, setSending] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [vendors, setVendors] = useState([]);
    const [vendorSearch, setVendorSearch] = useState('');

    const [newAnnouncement, setNewAnnouncement] = useState({
        title: '',
        message: '',
        target_audience: 'vendors',
        target_vendor_id: '',
        target_vendor_name: ''
    });

    useEffect(() => {
        fetchAnnouncements();
        fetchVendors();
    }, []);

    const fetchVendors = async () => {
        try {
            const res = await axios.get(`${API}/api/vendors`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setVendors(res.data);
        } catch (error) {
            console.error('Failed to fetch vendors:', error);
        }
    };

    const fetchAnnouncements = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/admin/announcements`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAnnouncements(res.data);
        } catch (error) {
            console.error('Failed to fetch announcements:', error);
            setAnnouncements([]);
        }
        setLoading(false);
    };

    const createAnnouncement = async () => {
        if (!newAnnouncement.title.trim() || !newAnnouncement.message.trim()) {
            toast.error('Please fill in all fields');
            return;
        }
        if (newAnnouncement.target_audience === 'specific_vendor' && !newAnnouncement.target_vendor_id) {
            toast.error('Please select a vendor');
            return;
        }

        setSending(true);
        try {
            await axios.post(`${API}/api/admin/announcements`, newAnnouncement, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Announcement created successfully!');
            setShowCreateForm(false);
            setNewAnnouncement({ title: '', message: '', target_audience: 'vendors', target_vendor_id: '', target_vendor_name: '' });
            setVendorSearch('');
            fetchAnnouncements();
        } catch (error) {
            toast.error('Failed to create announcement');
        }
        setSending(false);
    };

    const deleteAnnouncement = async (id) => {
        setDeleting(id);
        try {
            await axios.delete(`${API}/api/admin/announcements/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Announcement deleted');
            fetchAnnouncements();
        } catch (error) {
            toast.error('Failed to delete announcement');
        }
        setDeleting(null);
    };

    const getTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = Math.floor((now - date) / 1000);

        if (diff < 60) return 'Just now';
        if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
        if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
        return date.toLocaleDateString();
    };

    return (
        <DashboardLayout userRole="admin">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                            <Megaphone className="w-8 h-8 text-primary" />
                            Announcements
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Send announcements to vendors
                        </p>
                    </div>
                    <Button onClick={() => setShowCreateForm(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        New Announcement
                    </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-card rounded-xl border p-4">
                        <p className="text-sm text-muted-foreground">Total</p>
                        <p className="text-2xl font-bold mt-1">{announcements.length}</p>
                    </div>
                    <div className="bg-card rounded-xl border p-4">
                        <p className="text-sm text-muted-foreground">For Vendors</p>
                        <p className="text-2xl font-bold text-primary mt-1">
                            {announcements.filter(a => a.target_audience === 'vendors').length}
                        </p>
                    </div>
                    <div className="bg-card rounded-xl border p-4">
                        <p className="text-sm text-muted-foreground">For Customers</p>
                        <p className="text-2xl font-bold text-green-600 mt-1">
                            {announcements.filter(a => a.target_audience === 'customers').length}
                        </p>
                    </div>
                    <div className="bg-card rounded-xl border p-4">
                        <p className="text-sm text-muted-foreground">For All</p>
                        <p className="text-2xl font-bold text-blue-600 mt-1">
                            {announcements.filter(a => a.target_audience === 'all').length}
                        </p>
                    </div>
                </div>

                {/* Announcements List */}
                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="bg-card rounded-xl border p-6 animate-pulse">
                                <div className="h-5 bg-muted rounded w-1/3 mb-3"></div>
                                <div className="h-4 bg-muted rounded w-full mb-2"></div>
                                <div className="h-4 bg-muted rounded w-3/4"></div>
                            </div>
                        ))}
                    </div>
                ) : announcements.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-card rounded-xl border p-12 text-center"
                    >
                        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                            <Megaphone className="w-8 h-8 text-muted-foreground" />
                        </div>
                        <h3 className="font-semibold text-lg">No Announcements</h3>
                        <p className="text-muted-foreground mt-2 mb-4">
                            You haven't created any announcements yet
                        </p>
                        <Button onClick={() => setShowCreateForm(true)}>
                            <Plus className="w-4 h-4 mr-2" />
                            Create First Announcement
                        </Button>
                    </motion.div>
                ) : (
                    <div className="space-y-4">
                        {announcements.map((announcement, index) => (
                            <motion.div
                                key={announcement.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="bg-card rounded-xl border p-6"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <h3 className="font-semibold text-lg">{announcement.title}</h3>
                                            <Badge variant="outline" className={announcement.target_audience === 'specific_vendor' ? 'border-orange-300 text-orange-700 bg-orange-50' : ''}>
                                                {announcement.target_audience === 'specific_vendor' ? <User className="w-3 h-3 mr-1" /> : <Users className="w-3 h-3 mr-1" />}
                                                {announcement.target_audience === 'specific_vendor' ? announcement.target_vendor_name : announcement.target_audience}
                                            </Badge>
                                        </div>
                                        <p className="text-muted-foreground whitespace-pre-wrap">
                                            {announcement.message}
                                        </p>
                                        <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-4 h-4" />
                                                {getTimeAgo(announcement.created_at)}
                                            </span>
                                            <span>By: {announcement.created_by || 'Admin'}</span>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                        onClick={() => deleteAnnouncement(announcement.id)}
                                        disabled={deleting === announcement.id}
                                    >
                                        {deleting === announcement.id ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Trash2 className="w-4 h-4" />
                                        )}
                                    </Button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Create Announcement Modal */}
                <AnimatePresence>
                    {showCreateForm && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                            onClick={() => setShowCreateForm(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.9 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0.9 }}
                                className="bg-card rounded-2xl shadow-xl w-full max-w-lg"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="p-6 border-b">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-xl font-bold flex items-center gap-2">
                                            <Megaphone className="w-5 h-5 text-primary" />
                                            New Announcement
                                        </h2>
                                        <Button variant="ghost" size="icon" onClick={() => setShowCreateForm(false)}>
                                            <X className="w-5 h-5" />
                                        </Button>
                                    </div>
                                </div>

                                <div className="p-6 space-y-4">
                                    {/* Target Audience - Dropdown 1 */}
                                    <div>
                                        <label className="text-sm font-medium mb-1.5 block">Send To</label>
                                        <select
                                            value={newAnnouncement.target_audience === 'specific_vendor' ? 'vendors' : newAnnouncement.target_audience}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setNewAnnouncement({ ...newAnnouncement, target_audience: val, target_vendor_id: '', target_vendor_name: '' });
                                            }}
                                            className="w-full p-2.5 border rounded-lg text-sm bg-background focus:ring-2 ring-primary/20 focus:border-primary"
                                        >
                                            <option value="vendors">🏪 Vendors</option>
                                            <option value="customers">🛒 Customers</option>
                                            <option value="all">👥 All Users</option>
                                        </select>
                                    </div>

                                    {/* Vendor Picker - Dropdown 2 (only when Vendors selected) */}
                                    {(newAnnouncement.target_audience === 'vendors' || newAnnouncement.target_audience === 'specific_vendor') && (
                                        <div>
                                            <label className="text-sm font-medium mb-1.5 block">Select Vendor</label>
                                            <select
                                                value={newAnnouncement.target_vendor_id || '_all'}
                                                onChange={(e) => {
                                                    const vendorId = e.target.value;
                                                    if (vendorId === '_all') {
                                                        setNewAnnouncement({ ...newAnnouncement, target_audience: 'vendors', target_vendor_id: '', target_vendor_name: '' });
                                                    } else {
                                                        const vendor = vendors.find(v => v.id === vendorId);
                                                        setNewAnnouncement({
                                                            ...newAnnouncement,
                                                            target_audience: 'specific_vendor',
                                                            target_vendor_id: vendorId,
                                                            target_vendor_name: vendor?.business_name || vendor?.name || ''
                                                        });
                                                    }
                                                }}
                                                className="w-full p-2.5 border rounded-lg text-sm bg-background focus:ring-2 ring-primary/20 focus:border-primary"
                                            >
                                                <option value="_all">📢 All Vendors</option>
                                                {vendors.map(v => (
                                                    <option key={v.id} value={v.id}>🎯 {v.business_name || v.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {/* Title */}
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Title</label>
                                        <input
                                            type="text"
                                            placeholder="Announcement title"
                                            value={newAnnouncement.title}
                                            onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                                            className="w-full p-3 border rounded-lg focus:ring-2 ring-primary/20 focus:border-primary"
                                        />
                                    </div>

                                    {/* Message */}
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Message</label>
                                        <Textarea
                                            placeholder="Write your announcement message..."
                                            value={newAnnouncement.message}
                                            onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                                            rows={5}
                                        />
                                    </div>
                                </div>

                                <div className="p-6 border-t flex justify-end gap-3">
                                    <Button variant="outline" onClick={() => setShowCreateForm(false)}>
                                        Cancel
                                    </Button>
                                    <Button onClick={createAnnouncement} disabled={sending}>
                                        {sending ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Send className="w-4 h-4 mr-2" />
                                        )}
                                        Send Announcement
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </DashboardLayout>
    );
};

export default AdminAnnouncements;
